import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiFacebook, FiInstagram, FiYoutube, FiMail, FiPhone, FiMapPin, FiTwitter } from 'react-icons/fi';
import { FaFacebook, FaInstagram, FaYoutube, FaTwitter, FaLinkedin, FaTiktok } from 'react-icons/fa';
import { SITE_NAME, CONTACT_INFO, SOCIAL_LINKS } from '../../utils/constants';
import { useSettings } from '../../contexts/SettingsContext';
import { apiService } from '../../services/api';

// Social media icon mapping
const socialIconMap = {
  FaFacebook,
  FaInstagram,
  FaYoutube,
  FaTwitter,
  FaLinkedin,
  FaTiktok,
  FiFacebook,
  FiInstagram,
  FiYoutube,
  FiTwitter,
};

export default function Footer() {
  const currentYear = new Date().getFullYear();
  const { settings } = useSettings();
  const [footerData, setFooterData] = useState(null);

  // Default footer links (fallback)
  const defaultFooterLinks = {
    about: [
      { label: 'Our Story', href: '/about' },
      { label: 'Beliefs', href: '/about/beliefs' },
      { label: 'Leadership', href: '/about/leadership' },
      { label: 'Contact', href: '/contact' },
    ],
    resources: [
      { label: 'Sermons', href: '/sermons' },
      { label: 'Sunday Bulletin', href: '/bulletin' },
      { label: 'Give', href: '/give' },
      { label: 'Blog', href: '/blog' },
    ],
    connect: [
      { label: 'Small Groups', href: '/small-groups' },
      { label: 'Events', href: '/events' },
      { label: 'Livestream', href: '/livestream' },
      { label: 'Podcast', href: '/podcast' },
    ],
  };

  // Fetch footer data
  useEffect(() => {
    const fetchFooterData = async () => {
      try {
        const response = await apiService.getFooter();
        setFooterData(response.data);
      } catch (error) {
        console.error('Failed to fetch footer data:', error);
        // Fallback to default links on error
      }
    };

    fetchFooterData();
  }, []);

  // Use database columns or fallback to default
  const footerColumns = footerData?.columns || [
    { id: 1, title: 'About', links: defaultFooterLinks.about },
    { id: 2, title: 'Resources', links: defaultFooterLinks.resources },
    { id: 3, title: 'Connect', links: defaultFooterLinks.connect },
  ];

  const socialMediaLinks = footerData?.socialMedia || [];
  const copyrightText = footerData?.settings?.copyright_text || `© ${currentYear} The Church Gathered and Scattered, Inc. All rights reserved.`;
  const showSocialMedia = footerData?.settings?.show_social_media !== false;
  
  return (
    <footer className="bg-neutral pt-16 pb-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 mb-12">
          {/* Brand Section */}
          <div className="lg:col-span-1">
            <Link to="/" className="inline-block mb-4">
              {settings.logo_dark_url ? (
                <>
                {/*<img
                  src={settings.logo_dark_url}
                  alt={settings.site_name || SITE_NAME}
                  className="h-10 rounded-[6px] inline w-auto object-contain"
                />*/}
                <h4 className="font-serif text-xl inline text-primary">
                  {settings.site_name || SITE_NAME}
                </h4>
                </>
              ) : (
                <h3 className="font-serif text-2xl text-primary font-semibold">
                  {SITE_NAME}
                </h3>
              )}
            </Link>
            <div className="text-primary/70 text-[15px] space-y-2">
              <p className="flex items-start gap-2">
                <FiMapPin className="w-5 h-5 mt-0.5 flex-shrink-0" />
                <span>
                  <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent((settings.site_name || SITE_NAME) + ',' + CONTACT_INFO.address + ',' + CONTACT_INFO.city + ',' + CONTACT_INFO.state + ' ' + CONTACT_INFO.zip)}`} target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">
                  {CONTACT_INFO.address}<br />
                  {CONTACT_INFO.city}, {CONTACT_INFO.state} {CONTACT_INFO.zip}
                  </a>
                </span>
              </p>
              <p className="flex items-center gap-2">
                <FiPhone className="w-5 h-5 flex-shrink-0" />
                <a 
                  href={`tel:${CONTACT_INFO.phone.replace(/[^\d]/g, '')}`}
                  className="hover:text-primary transition-colors"
                >
                  {CONTACT_INFO.phone}
                </a>
              </p>
              <p className="flex items-center gap-2">
                <FiMail className="w-5 h-5 flex-shrink-0" />
                <a 
                  href={`mailto:${CONTACT_INFO.email}`}
                  className="hover:text-primary transition-colors"
                >
                  {CONTACT_INFO.email}
                </a>
              </p>
            </div>
          </div>

          {/* Dynamic Footer Columns */}
          {footerColumns.map((column) => (
            <div key={column.id}>
              <h4 className="text-primary font-semibold mb-4">{column.title}</h4>
              <ul className="space-y-2 list-none p-0">
                {column.links.map((link) => (
                  <li key={link.id || link.href}>
                    <Link
                      to={link.url || link.href}
                      className="text-primary/70 hover:text-primary hover:pl-1 transition-all text-[15px]"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        
        {/* Bottom Bar */}
        <div className="pt-8 border-t border-primary/10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-primary/60">
            {copyrightText}
          </p>

          <div className="flex items-center gap-4">
            <p className="text-sm text-primary/60">
              <a href="/privacy" className="hover:text-primary hover:underline transition-colors">
                Privacy Policy
              </a>
            </p>
            {/* Social Links */}
            {showSocialMedia && (
              <div className="flex items-center gap-4">
                {socialMediaLinks.length > 0 ? (
                  socialMediaLinks.map((social) => {
                    const IconComponent = socialIconMap[social.icon] || FiFacebook;
                    return (
                      <a
                        key={social.id}
                        href={social.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary/60 hover:text-accent transition-colors"
                        aria-label={social.platform}
                      >
                        <IconComponent size={20} />
                      </a>
                    );
                  })
                ) : (
                  // Fallback to default social links
                  <>
                    {SOCIAL_LINKS.facebook && (
                      <a
                        href={SOCIAL_LINKS.facebook}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary/60 hover:text-accent transition-colors"
                        aria-label="Facebook"
                      >
                        <FiFacebook size={20} />
                      </a>
                    )}
                    {SOCIAL_LINKS.instagram && (
                      <a
                        href={SOCIAL_LINKS.instagram}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary/60 hover:text-accent transition-colors"
                        aria-label="Instagram"
                      >
                        <FiInstagram size={20} />
                      </a>
                    )}
                    {SOCIAL_LINKS.youtube && (
                      <a
                        href={SOCIAL_LINKS.youtube}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary/60 hover:text-accent transition-colors"
                        aria-label="YouTube"
                      >
                        <FiYoutube size={20} />
                      </a>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </footer>
  );
}
